import { useState, useEffect, useRef, useCallback } from "react";
import { api, apiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, Trash2, Download, Loader2, Layers, MapPin, Sparkles, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { cleanName } from "@/lib/format";

export default function StepGeology({ project, reload, onNext, onBack }) {
  const pid = project.id;
  const areas = (project.areas || []).filter((a) => a.status !== "error");
  const [geo, setGeo] = useState({});   // aid -> geology summary (or null)
  const [busy, setBusy] = useState({});  // aid -> bool
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchGeo = useCallback(async (aid) => {
    try {
      const { data } = await api.get(`/projects/${pid}/areas/${aid}/geology`);
      setGeo((m) => ({ ...m, [aid]: { ...data.geology, map: data.map } }));
    } catch (_) {
      setGeo((m) => ({ ...m, [aid]: null }));
    }
  }, [pid]);

  useEffect(() => { areas.forEach((a) => fetchGeo(a.area_id)); /* eslint-disable-next-line */ }, [pid]);

  const setAreaBusy = (aid, v) => setBusy((b) => ({ ...b, [aid]: v }));

  const upload = async (aid, files) => {
    const f = Array.from(files || [])[0];
    if (!f) return;
    if (!/\.(geojson|json|kml|kmz|zip)$/i.test(f.name)) return toast.error("Use GeoJSON / KML / KMZ / Shapefile .zip");
    setAreaBusy(aid, true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/projects/${pid}/areas/${aid}/geology`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGeo((m) => ({ ...m, [aid]: { units: data.units, dominant: data.dominant, count: data.count, field: data.field, setting: data.setting, map: data.map } }));
      toast.success(`Geological map generated · ${data.count} unit(s)`);
      reload && reload();
    } catch (e) { toast.error(apiError(e)); } finally { setAreaBusy(aid, false); }
  };

  const aiGenerate = async (aid, silent = false) => {
    setAreaBusy(aid, true);
    try {
      const { data } = await api.post(`/projects/${pid}/areas/${aid}/geology/ai-generate`);
      setGeo((m) => ({ ...m, [aid]: { units: data.units, dominant: data.dominant, count: data.count, field: data.field, setting: data.setting, map: data.map, ai_generated: true, source: data.source } }));
      if (!silent) toast.success(`AI geological map generated · ${data.count} indicative unit(s)`);
      return true;
    } catch (e) { if (!silent) toast.error(apiError(e)); return false; }
    finally { setAreaBusy(aid, false); }
  };

  const del = async (aid) => {
    try {
      await api.delete(`/projects/${pid}/areas/${aid}/geology`);
      setGeo((m) => ({ ...m, [aid]: null }));
      toast.success("Geology removed");
      reload && reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const generateForAll = async () => {
    const pending = areas.filter((a) => !geo[a.area_id]);
    if (!pending.length) return toast.info("Every licence already has a geological interpretation");
    setBulkBusy(true);
    let ok = 0;
    for (const a of pending) { if (await aiGenerate(a.area_id, true)) ok += 1; }
    setBulkBusy(false);
    ok ? toast.success(`AI geology generated for ${ok} licence(s)`) : toast.error("AI geology generation failed");
    reload && reload();
  };

  const withGeo = areas.filter((a) => geo[a.area_id]).length;

  return (
    <div className="space-y-6" data-testid="step-geology">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
        <div className="min-w-0 lg:max-w-xl">
          <h2 className="text-lg font-bold text-foreground tracking-tight">Reference &amp; Geology</h2>
          <p className="text-sm text-muted-foreground">
            Add any published or interpreted geology to guide the prospectivity model - or let AI generate an
            indicative Local Geological Setting from each licence&apos;s location and target commodity.
            This step is optional; you can continue without it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 lg:justify-end shrink-0">
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="geology-progress">{withGeo}/{areas.length} with geology</span>
          <Button data-testid="geology-generate-all-btn" onClick={generateForAll} disabled={bulkBusy || areas.length === 0}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-5 text-sm font-semibold">
            {bulkBusy ? <><Loader2 size={16} className="mr-2 animate-spin" />Generating for all…</> : <><Sparkles size={16} className="mr-2" />AI-Generate for All Licences</>}
          </Button>
        </div>
      </div>

      {areas.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-xl py-16 text-center text-muted-foreground" data-testid="geology-no-areas">
          <MapPin size={38} className="mx-auto mb-3" />
          Upload at least one licence boundary in the previous step first.
        </div>
      )}

      <div className="space-y-4" data-testid="geology-area-list">
        {areas.map((a) => (
          <GeologyAreaCard key={a.area_id} area={a} pid={pid} geo={geo[a.area_id]} busy={!!busy[a.area_id]}
            onUpload={(files) => upload(a.area_id, files)} onAi={() => aiGenerate(a.area_id)} onDelete={() => del(a.area_id)} />
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" className="border-border h-11 px-5 text-sm font-semibold" onClick={onBack} data-testid="geology-back-btn">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
        <Button data-testid="geology-continue-btn" onClick={onNext}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-sm font-semibold">
          Continue to Analysis <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}

function GeologyAreaCard({ area, pid, geo, busy, onUpload, onAi, onDelete }) {
  const inputRef = useRef(null);
  const aid = area.area_id;
  return (
    <div className="bg-card border border-border rounded-xl shadow-sm p-5" data-testid={`geology-area-card-${aid}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground grid place-items-center font-extrabold text-sm shrink-0">{aid}</div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground truncate">{cleanName(area.name)}</div>
            <div className="text-[11px] text-muted-foreground">
              {geo ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> {geo.count || (geo.units || []).length} unit(s) mapped{geo.ai_generated ? " · AI indicative" : ""}</span>
                   : "No geology yet"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 lg:justify-end shrink-0">
          <input ref={inputRef} type="file" accept=".geojson,.json,.kml,.kmz,.zip" className="hidden"
            data-testid={`geology-file-input-${aid}`} onChange={(e) => { const fs = e.target.files; e.target.value = ""; onUpload(fs); }} />
          <Button data-testid={`geology-upload-btn-${aid}`} onClick={() => inputRef.current?.click()} disabled={busy} variant="outline"
            className="border-border h-10 px-4 text-sm font-semibold">
            {busy ? <><Loader2 size={15} className="mr-2 animate-spin" />Processing…</> : <><UploadCloud size={15} className="mr-2" />{geo ? "Replace" : "Upload"}</>}
          </Button>
          <Button data-testid={`geology-ai-btn-${aid}`} onClick={onAi} disabled={busy}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-4 text-sm font-semibold">
            {busy ? <><Loader2 size={15} className="mr-2 animate-spin" />Generating…</> : <><Sparkles size={15} className="mr-2" />AI-Generate</>}
          </Button>
          {geo && (
            <>
              <Button data-testid={`geology-download-btn-${aid}`} variant="outline" className="border-border h-10 px-3"
                onClick={() => downloadBlob(`/projects/${pid}/areas/${aid}/geology/map`, `${aid}_geology.png`).catch((e) => toast.error(apiError(e)))}>
                <Download size={15} />
              </Button>
              <Button data-testid={`geology-delete-btn-${aid}`} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-10 px-3" onClick={onDelete}>
                <Trash2 size={15} />
              </Button>
            </>
          )}
        </div>
      </div>

      {geo?.ai_generated && (
        <div className="mt-4 flex items-center gap-2 text-[11px] font-semibold text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`geology-ai-badge-${aid}`}>
          <Sparkles size={13} className="shrink-0" /> AI-interpreted · indicative regional geology - verify in the field before any use.
        </div>
      )}

      {geo ? (
        <div className="mt-4 grid lg:grid-cols-2 gap-4" data-testid={`geology-result-${aid}`}>
          <div className="bg-muted border border-border rounded-xl p-3">
            {geo.map ? <img src={geo.map} alt={`Local geological map ${aid}`} className="w-full rounded-md" data-testid={`geology-map-img-${aid}`} />
                     : <div className="py-16 text-center text-muted-foreground text-sm">Map preview unavailable</div>}
          </div>
          <div className="space-y-3">
            <div className="bg-muted border border-border rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Mapped Units ({geo.count || (geo.units || []).length})</div>
              <div className="space-y-1.5" data-testid={`geology-units-${aid}`}>
                {(geo.units || []).map((u, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="h-3.5 w-3.5 rounded-sm border border-border shrink-0" style={{ background: u.color }} />
                    <span className="text-foreground truncate flex-1">{u.name}</span>
                    <span className="text-muted-foreground text-xs tabular-nums">{typeof u.pct === "number" ? `${u.pct.toFixed(0)}%` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-muted border border-border rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Local Geological Setting</div>
              <p className="text-sm text-foreground leading-relaxed" data-testid={`geology-setting-${aid}`}>{geo.setting}</p>
            </div>
          </div>
        </div>
      ) : (!busy && (
        <div className="mt-4 border-2 border-dashed border-border rounded-xl py-8 px-4 text-center text-sm text-muted-foreground" data-testid={`geology-empty-${aid}`}>
          <Layers size={28} className="mx-auto mb-2 text-muted-foreground" />
          Upload a geological dataset, or tap <span className="font-semibold text-primary">AI-Generate</span> for an indicative Local Geological Setting.
        </div>
      ))}
    </div>
  );
}
